/**
 * Slider: a native range input with its value shown in an <output> (cockatrice-ui `src/ui/Slider.jsx`, ported to
 * hyperscript; PLAN.md §0).
 * For settings with a sensible range, such as Text size (14–26 px) or line width.
 * onChange(number, event) while dragging. `unit` (" px") or `format(value)` for the readout.
 */
import './css/slider.css';
import { createEffect, createSignal, mergeProps, splitProps } from 'solid-js';
import { useFieldAttrs, wrapControl } from './Field.ts';
import { component, el, spread, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';

export interface SliderProps extends Attributes {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** Lowest value. Default 0. */
  readonly min?: number | undefined;
  /** Highest value. Default 100. */
  readonly max?: number | undefined;
  /** Step. Default 1. */
  readonly step?: number | undefined;
  /** Controlled value. */
  readonly value?: number | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: number | undefined;
  /** Called while dragging. */
  readonly onChange?: ((value: number, event: InputEvent) => void) | undefined;
  /** Appended to the readout, e.g. " px". */
  readonly unit?: string | undefined;
  /** Formats the readout; overrides unit. */
  readonly format?: ((value: number) => string) | undefined;
  /** Show the value. Default true. */
  readonly showValue?: boolean | undefined;
  /** Show min and max at the ends. */
  readonly showRange?: boolean | undefined;
  /** Disables the slider. */
  readonly disabled?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

/** The JSX passes the field's error on, never required (a range input has no empty state). */
const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass', 'required'] as const;

export default function Slider(props: SliderProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () => component(SliderInput, rest));
}

function SliderInput(props: SliderProps): Child {
  const p = mergeProps({ min: 0, max: 100, step: 1, unit: '', showValue: true }, props);
  const [local, rest] = splitProps(p, [
    'id',
    'min',
    'max',
    'step',
    'value',
    'defaultValue',
    'onChange',
    'unit',
    'format',
    'showValue',
    'showRange',
    'error',
    'invalid',
    'disabled',
    'class',
    'aria-describedby',
  ]);
  const f = useFieldAttrs(local);
  const [val, setVal] = createSignal(local.value ?? local.defaultValue ?? local.min);
  createEffect(() => {
    if (local.value !== undefined) setVal(local.value);
  });

  const text = (v: number): string => (local.format ? local.format(v) : `${v}${local.unit}`);
  const pct = (): number => ((val() - local.min) / (local.max - local.min || 1)) * 100;

  return el(
    'div',
    { class: () => ['ui-slider', f.disabled() && 'is-disabled', local.class].filter(Boolean).join(' ') },
    el(
      'div',
      { class: 'ui-slider__row' },
      when(
        () => local.showRange === true,
        () => el('span', { class: 'ui-slider__end', 'aria-hidden': 'true' }, () => text(local.min)),
      ),
      el(
        'input',
        spread(rest, {
          id: () => f.id(),
          type: 'range',
          class: 'ui-slider__input',
          style: () => ({ '--fill': `${pct()}%` }),
          min: () => local.min,
          max: () => local.max,
          step: () => local.step,
          value: val,
          disabled: () => f.disabled(),
          'aria-invalid': () => (f.invalid() ? 'true' : undefined),
          'aria-describedby': () => f.describedBy(),
          'aria-valuetext': () => text(val()),
          onInput: (event: On<InputEvent, HTMLInputElement>) => {
            const v = Number(event.currentTarget.value);
            setVal(v);
            local.onChange?.(v, event);
          },
        }),
      ),
      when(
        () => local.showRange === true,
        () => el('span', { class: 'ui-slider__end', 'aria-hidden': 'true' }, () => text(local.max)),
      ),
      when(
        () => local.showValue,
        () => el('output', { class: 'ui-slider__value', for: () => f.id() }, () => text(val())),
      ),
    ),
  );
}
